import { Component, ojs, app, v, state, component } from "modular-openscriptjs";
import {
    pageInfo,
    scrollToTop,
    monetary,
    fireEvent,
    showActionButton,
    eParse,
    cartEmpty,
    HIDDEN_PLAN_FEATURES,
} from "../../helpers";
import { PAGES, uc, api } from "../../declarations";
import { tool } from "../../fotastart";
import { $e } from "../../ojs.events";
import DeliveryAddress from "../DeliveryAddress";
import Swal from "sweetalert2";

const h = app("h");
ojs(DeliveryAddress);

const getProvisionBool = (subVal, key, defaultVal = false) => {
    const subProv = subVal?.provisions?.find((p) => p.key === key);
    if (subProv) return String(subProv.value) === "1" || String(subProv.value) === "true";
    const planProv = subVal?.plan?.provisions?.find((p) => p.key === key);
    if (planProv) return String(planProv.value) === "1" || String(planProv.value) === "true";
    return defaultVal;
};

export default class Checkout extends Component {
    trialModalHolder = null;
    actionButtonShown = false;

    deliveryCostLoading = state(false);
    deliveryInfo = state(null);
    shouldSchedule = state(false);
    initialized = false;

    explorePlans() {
        fireEvent($e.subscription.needs.plans);
    }

    async fetchDeliveryPrice() {
        const addresses = uc.addresses.value;
        if (!addresses || addresses.length === 0) return;

        if (cartEmpty()) return;

        const activeAddress = addresses[addresses.length - 1];
        if (!activeAddress || !activeAddress.id) return;

        if (this.deliveryCostLoading.value === true) return;

        this.deliveryCostLoading.value = true;

        try {
            const res = await api.get(
                `/orders/delivery-price/d/${activeAddress.id}`,
            );
            if (res && res.data) {
                this.deliveryInfo.value = res.data;

                // Select the first company
                const companies = res.data.companies;
                const companyKeys = Object.keys(companies);
                if (companyKeys.length > 0) {
                    const firstCompany = companies[companyKeys[0]];
                    uc.logisticsCompany.value = firstCompany;

                    // Automatically choose a default fleet type from available ones
                    const fleetTypes = firstCompany.fleet_types || [];
                    if (fleetTypes.length > 0) {
                        uc.fleetType.value = fleetTypes[0].name;
                    } else if (firstCompany.monetary_fee_per_vehicle) {
                        const vehicles = Object.keys(
                            firstCompany.monetary_fee_per_vehicle,
                        );
                        if (vehicles.length > 0) {
                            uc.fleetType.value = vehicles[0];
                        }
                    }
                }
            }
        } catch (e) {
            console.error("Error fetching delivery price:", e);
        } finally {
            this.deliveryCostLoading.value = false;
        }
    }

    showTrialUsageInfo() {
        if (this.trialModalHolder) {
            this.trialModalHolder.hide();
        }

        const sub = uc.subscription.value;
        if (!sub || !sub.id) return;

        let options = new tool.ModalOptions();
        options.buttons.right.visible = false;

        // Calculate days used
        const totalMs = new Date(sub.expires_at) - new Date(sub.started_at);
        const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));
        const elapsedMs = new Date() - new Date(sub.started_at);
        const usedDays = Math.max(
            0,
            Math.ceil(elapsedMs / (1000 * 60 * 60 * 24)),
        );
        const displayUsedDays = Math.min(usedDays, totalDays);
        const durationPct =
            totalDays > 0 ? (displayUsedDays / totalDays) * 100 : 0;

        const progressCards = [];

        // 1. Duration Progress (if starts_at/expires_at exist)
        progressCards.push(
            h.div(
                { class: "col-12" },
                h.div(
                    { class: "p-3 bg-light rounded-3 text-center" },
                    h.small(
                        {
                            class: "text-muted d-block mb-1 text-uppercase fw-semibold",
                        },
                        "Duration (Days Used)",
                    ),
                    h.div(
                        { class: "fw-bold text-dark font-monospace" },
                        `${displayUsedDays} / ${totalDays} Days`,
                    ),
                    h.div(
                        { class: "progress mt-2", style: "height: 6px;" },
                        h.div({
                            class: "progress-bar",
                            style: `background-color: #0dcaf0; width: ${durationPct}%;`,
                        }),
                    ),
                ),
            ),
        );

        // Map feature names to user-friendly titles, progress colors, and labels
        const featureConfig = {
            number_of_orders: {
                title: "Completed Orders",
                color: "#ffc107",
                suffix: "Orders",
            },
            ai_credits: {
                title: "AI Credits",
                color: "#973e87",
                suffix: "Credits",
            },
            virtual_distance: {
                title: "Prepaid Delivery (vKM)",
                color: "#5d5fef",
                suffix: "vKM",
            },
        };

        if (sub.usages && Array.isArray(sub.usages)) {
            sub.usages.forEach((usage) => {
                const config = featureConfig[usage.feature_name];
                if (!config || Number(usage.limit) <= 0) return;
                if (HIDDEN_PLAN_FEATURES.includes(usage.feature_name)) return;

                const usedVal = Number(usage.used);
                const limitVal = Number(usage.limit);
                const pct = limitVal > 0 ? (usedVal / limitVal) * 100 : 0;

                progressCards.push(
                    h.div(
                        { class: "col-12" },
                        h.div(
                            { class: "p-3 bg-light rounded-3 text-center" },
                            h.small(
                                {
                                    class: "text-muted d-block mb-1 text-uppercase fw-semibold",
                                },
                                config.title,
                            ),
                            h.div(
                                { class: "fw-bold text-dark font-monospace" },
                                `${usedVal} / ${limitVal} ${config.suffix}`,
                            ),
                            h.div(
                                {
                                    class: "progress mt-2",
                                    style: "height: 6px;",
                                },
                                h.div({
                                    class: "progress-bar",
                                    style: `background-color: ${config.color}; width: ${pct}%;`,
                                }),
                            ),
                        ),
                    ),
                );
            });
        }

        let modalBody = h.div(
            { class: "p-2 text-dark" },
            h.p(
                { class: "text-muted small mb-4" },
                `Track your current usage relative to the subscription allowance for ${sub.plan?.name || "your plan"}.`,
            ),
            h.div({ class: "row g-3" }, ...progressCards),
        );

        let modal = tool.modal("Subscription Usage Stats", modalBody, options);
        this.trialModalHolder = modal.modal.object;
        this.trialModalHolder.show();
    }

    render(...args) {
        pageInfo(PAGES.CHECKOUT);
        scrollToTop();

        return h.main(
            { class: "checkout-page pb-5 pt-3 py-md-5 container mb-5" },
            h.div(
                { class: "row g-4" },
                h.div(
                    { class: "col-12 col-lg-8 d-flex flex-column gap-3" },

                    // 1. Delivery Address Card
                    h.DeliveryAddress(uc.addresses),

                    // 2. Subscription Guarded Section
                    v(uc.subscription, (sub) => {
                        const isSubscribed = sub.value && sub.value.id;

                        return h.div(
                            { class: "d-flex flex-column gap-3" },
                            // Virtual Delivery Distance Card
                            v(uc.addresses, (addresses) => {
                                if (
                                    !addresses.value ||
                                    addresses.value.length === 0
                                ) {
                                    return null;
                                }
                                return h.div(
                                    {
                                        class: "card border-0 shadow-sm rounded-4 p-4 bg-white",
                                    },
                                    h.h6(
                                        {
                                            class: "fw-bold text-muted text-uppercase small mb-3 d-flex align-items-center gap-2",
                                        },
                                        h.i({ class: "bi bi-truck" }),
                                        "Delivery Cost",
                                    ),
                                    v(this.deliveryCostLoading, (loading) => {
                                        if (loading.value) {
                                            return h.div(
                                                {
                                                    class: "d-flex flex-column align-items-center justify-content-center py-4",
                                                },
                                                h.div({
                                                    class: "spinner-border text-primary mb-2",
                                                    role: "status",
                                                    style: "color: #5d5fef;",
                                                }),
                                                h.span(
                                                    {
                                                        class: "text-muted small",
                                                    },
                                                    "Calculating route and fees...",
                                                ),
                                            );
                                        }

                                        return v(this.deliveryInfo, (info) => {
                                            if (!info || !info.companies) {
                                                return h.div(
                                                    {
                                                        class: "text-center text-muted py-3 small",
                                                    },
                                                    "No delivery options available for this address.",
                                                );
                                            }

                                            // Get first company
                                            const companies = info.companies;
                                            const companyKeys =
                                                Object.keys(companies);
                                            if (companyKeys.length === 0) {
                                                return h.div(
                                                    {
                                                        class: "text-center text-muted py-3 small",
                                                    },
                                                    "No logistics companies operating in this area.",
                                                );
                                            }

                                            const firstCompany =
                                                companies[companyKeys[0]];
                                            const fleet =
                                                uc.fleetType.value ||
                                                Object.keys(
                                                    firstCompany.monetary_fee_per_vehicle,
                                                )[0];

                                            const fee =
                                                firstCompany
                                                    .monetary_fee_per_vehicle[
                                                    fleet
                                                ] || 0;
                                            const vkmRequired =
                                                firstCompany
                                                    .vkm_required_per_vehicle?.[
                                                    fleet
                                                ] || 0;
                                            const paymentType =
                                                firstCompany
                                                    .payment_type_per_vehicle?.[
                                                    fleet
                                                ] || "monetary";
                                            const savings =
                                                firstCompany
                                                    .savings_per_vehicle?.[
                                                    fleet
                                                ] || fee;

                                            const getProvision = (
                                                subVal,
                                                key,
                                                defaultVal = 0,
                                            ) => {
                                                const subProv =
                                                    subVal?.provisions?.find(
                                                        (p) => p.key === key,
                                                    );
                                                if (subProv)
                                                    return Number(
                                                        subProv.value,
                                                    );
                                                const planProv =
                                                    subVal?.plan?.provisions?.find(
                                                        (p) => p.key === key,
                                                    );
                                                if (planProv)
                                                    return Number(
                                                        planProv.value,
                                                    );
                                                return defaultVal;
                                            };

                                            const minPurchase = getProvision(
                                                sub.value,
                                                "minimal_purchase_size",
                                                0,
                                            );

                                            const maxItemsLimit = getProvision(
                                                sub.value,
                                                "maximum_items_per_order",
                                                0,
                                            );

                                            const itemsCount = uc.cart.value ? (
                                                Array.isArray(uc.cart.value)
                                                    ? uc.cart.value.length
                                                    : Object.keys(uc.cart.value).length
                                            ) : 0;

                                            let cartTotal = 0;
                                            if (uc.cart.value) {
                                                const items = Array.isArray(
                                                    uc.cart.value,
                                                )
                                                    ? uc.cart.value
                                                    : Object.values(
                                                          uc.cart.value,
                                                      );
                                                items.forEach((item) => {
                                                    cartTotal +=
                                                        Number(item.qty) *
                                                        Number(item.price);
                                                });
                                            }

                                            const vkmUsage =
                                                sub.value?.usages?.find(
                                                    (u) =>
                                                        u.feature_name ===
                                                        "virtual_distance",
                                                );
                                            const currentVkm = vkmUsage
                                                ? Math.max(
                                                      0,
                                                      Number(vkmUsage.limit) -
                                                          Number(vkmUsage.used),
                                                  )
                                                : 0;

                                            return h.div(
                                                {
                                                    class: "d-flex flex-column gap-3",
                                                },
                                                 isSubscribed 
                                                     ? h.div(
                                                           {
                                                               class: "row g-3 align-items-center",
                                                           },
                                                           h.div(
                                                               {
                                                                   class: "col-6 text-center border-end",
                                                               },
                                                               h.span(
                                                                   {
                                                                       class: "text-muted small d-block mb-1",
                                                                   },
                                                                   "vKM Required",
                                                               ),
                                                               h.h3(
                                                                   {
                                                                       class: "fw-bold text-dark mb-0 font-monospace",
                                                                   },
                                                                   `${vkmRequired} vKM`,
                                                               ),
                                                           ),
                                                           h.div(
                                                               {
                                                                   class: "col-6 text-center",
                                                               },
                                                               h.span(
                                                                   {
                                                                       class: "text-muted small d-block mb-1",
                                                                   },
                                                                   "Current Available",
                                                               ),
                                                               h.h3(
                                                                   {
                                                                       class: "fw-bold text-primary mb-0 font-monospace",
                                                                       style: "color: #5d5fef;",
                                                                   },
                                                                   `${currentVkm.toFixed(1)} vKM`,
                                                               ),
                                                           ),
                                                       )
                                                     : h.div(
                                                           { class: "d-flex justify-content-between align-items-center text-start" },
                                                           h.span({ class: "text-muted fw-semibold" }, "Fee"),
                                                           h.h4({ class: "fw-bold text-dark mb-0 font-monospace" }, `KES ${Number(fee).toLocaleString()}`)
                                                       ),

                                                // Alert banner based on payment type
                                                h.call(() => {
                                                    if (!isSubscribed) {
                                                        return h.div(
                                                            {
                                                                class: "card border-0 rounded-4 p-4 text-center cursor-pointer hover-scale transition-all animated-cta-card",
                                                                onclick: this.method("explorePlans")
                                                            },
                                                            h.style(`
                                                                @keyframes gradientShift {
                                                                    0% { background-position: 0% 50%; }
                                                                    50% { background-position: 100% 50%; }
                                                                    100% { background-position: 0% 50%; }
                                                                }
                                                                .animated-cta-card {
                                                                    background: linear-gradient(-45deg, #5d5fef, #8247e9, #4f46e5, #6366f1);
                                                                    background-size: 300% 300%;
                                                                    animation: gradientShift 8s ease infinite;
                                                                    border: none !important;
                                                                    box-shadow: 0 8px 24px rgba(93, 95, 239, 0.25) !important;
                                                                }
                                                                .animated-cta-card:hover {
                                                                    transform: translateY(-3px) scale(1.01) !important;
                                                                    box-shadow: 0 12px 30px rgba(93, 95, 239, 0.4) !important;
                                                                }
                                                            `),
                                                            h.div(
                                                                { class: "d-flex flex-column align-items-center gap-2" },
                                                                h.i({ class: "bi bi-gem text-white fs-3 mb-1" }),
                                                                h.h6(
                                                                    { class: "fw-extrabold text-white mb-1 fs-5" },
                                                                    `Save KES ${Number(savings).toLocaleString()} Before You Checkout!`
                                                                ),
                                                                h.p(
                                                                    { class: "text-white text-opacity-90 small mb-0", style: "font-size: 0.9rem; line-height: 1.5;" },
                                                                    `Skip paying the KES ${Number(fee).toLocaleString()} delivery fee.`,
                                                                    h.br(),
                                                                    h.span(
                                                                        { class: "fw-bold text-white text-decoration-underline d-inline-block mt-2 fs-6", style: "letter-spacing: 0.5px;" },
                                                                        "Subscribe →"
                                                                    )
                                                                )
                                                            )
                                                        );
                                                    }

                                                    switch (paymentType) {
                                                        case "virtual_distance":
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-success border-0 p-3 d-flex gap-3 align-items-center mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-check-circle-fill fs-4 text-success",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Paid with vKM",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "small mb-0 lh-base",
                                                                        },
                                                                        `Your subscription covers this delivery completely. 0 KES delivery fee will be charged.`,
                                                                    ),
                                                                ),
                                                            );
                                                        case "monetary_small_cart":
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-warning border-0 p-3 d-flex gap-3 align-items-start mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-exclamation-triangle-fill fs-4 text-warning",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Minimum Order Size Not Met",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "text-secondary small mb-0 lh-base",
                                                                        },
                                                                        `Reach KES ${minPurchase.toLocaleString()} to use your vKM. You're currently at KES ${cartTotal.toLocaleString()}.`,
                                                                    ),
                                                                ),
                                                            );
                                                        case "monetary_out_of_radius":
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-danger border-0 p-3 d-flex gap-3 align-items-start mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-exclamation-octagon-fill fs-4 text-danger",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Out of Delivery Radius",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "text-secondary small mb-0 lh-base",
                                                                        },
                                                                        `One or more items in your cart exceed your subscription's delivery radius. A delivery fee of KES ${fee} will be charged.`,
                                                                    ),
                                                                ),
                                                            );
                                                        case "monetary_items_limit":
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-danger border-0 p-3 d-flex gap-3 align-items-start mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-exclamation-octagon-fill fs-4 text-danger",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Order Items Limit Exceeded",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "text-secondary small mb-0 lh-base",
                                                                        },
                                                                        `Your cart has ${itemsCount} items, which exceeds your subscription's maximum items limit of ${maxItemsLimit} items. A delivery fee of KES ${fee} will be charged.`,
                                                                    ),
                                                                ),
                                                            );
                                                        default:
                                                            return h.div(
                                                                {
                                                                    class: "alert alert-info border-0 p-3 d-flex gap-3 align-items-start mb-0",
                                                                },
                                                                h.i({
                                                                    class: "bi bi-info-circle-fill fs-4 text-info",
                                                                }),
                                                                h.div(
                                                                    h.h6(
                                                                        {
                                                                            class: "fw-bold text-dark mb-1",
                                                                        },
                                                                        "Delivery Fee Charged",
                                                                    ),
                                                                    h.p(
                                                                        {
                                                                            class: "text-secondary small mb-0 lh-base",
                                                                        },
                                                                        `You do not have enough vKM remaining to cover this delivery. You will pay a delivery fee of KES ${fee}.`,
                                                                    ),
                                                                ),
                                                            );
                                                    }
                                                }),
                                            );
                                        });
                                    }),
                                );
                            }),

                            // Trial/Subscription Progress Card
                            isSubscribed
                                ? h.div(
                                      {
                                          class: "card border-0 shadow-sm rounded-4 p-4 bg-white",
                                          style: "border: 1px solid rgba(93, 95, 239, 0.08)!important;",
                                      },
                                      h.div(
                                          {
                                              class: "d-flex flex-column gap-3 text-start",
                                          },
                                          h.div(
                                              {
                                                  class: "d-flex justify-content-between align-items-center flex-wrap gap-2",
                                              },
                                              h.div(
                                                  {
                                                      class: "d-flex gap-2 flex-column",
                                                  },
                                                  h.span(
                                                      {
                                                          class: `rounded fw-bold py-1 px-3 ${
                                                              sub.value.trial_days > 0
                                                                  ? "bg-info bg-opacity-10 text-info border-info border-opacity-15"
                                                                  : "bg-success bg-opacity-10 text-success border-success border-opacity-15"
                                                          }`,
                                                          style: "font-size: 0.72rem;",
                                                      },
                                                      sub.value.trial_days > 0
                                                          ? "Active Trial"
                                                          : "Active Subscription",
                                                  ),
                                                  h.h6(
                                                      {
                                                          class: "fw-bold mb-0 text-dark",
                                                      },
                                                      sub.value.plan?.name ||
                                                          "Premium Plan",
                                                  ),
                                              ),
                                              h.div(
                                                  {
                                                      class: "d-flex gap-2 align-items-center",
                                                  },
                                                  h.button(
                                                      {
                                                          class: "btn btn-sm rounded-pill px-3 fw-bold",
                                                          onclick:
                                                              this.method(
                                                                  "explorePlans",
                                                              ),
                                                      },

                                                      sub.value.plan?.slug ===
                                                          "basic-plan"
                                                          ? "Upgrade Plan"
                                                          : "Change Plan",
                                                      sub.value.plan?.slug ===
                                                          "basic-plan"
                                                          ? {
                                                                class: "btn-outline-primary",
                                                            }
                                                          : {
                                                                class: "text-primary",
                                                            },
                                                  ),
                                                  h.button(
                                                      {
                                                          class: "btn btn-link text-muted p-1 border-0 d-flex align-items-center justify-content-center",
                                                          onclick:
                                                              this.method(
                                                                  "showTrialUsageInfo",
                                                              ),
                                                      },
                                                      h.i({
                                                          class: "bi bi-info-circle fs-5",
                                                      }),
                                                  ),
                                              ),
                                          ),
                                          // Custom Upgrade Banner / Message
                                          sub.value.plan?.slug === "basic-plan"
                                              ? h.div(
                                                    {
                                                        class: "p-3 rounded-3 mt-1 d-flex gap-2 align-items-start text-start",
                                                        style: "background-color: rgba(93, 95, 239, 0.05); border: 1px dashed rgba(93, 95, 239, 0.2);",
                                                    },
                                                    h.i({
                                                        class: "bi bi-stars text-primary fs-5 mt-0.5",
                                                        style: "color: #5d5fef;",
                                                    }),
                                                    h.div(
                                                        h.strong(
                                                            {
                                                                class: "d-block text-dark small mb-0.5",
                                                            },
                                                            "Get the best from Carata!",
                                                        ),
                                                        h.p(
                                                            {
                                                                class: "text-secondary small mb-0",
                                                                style: "font-size: 0.82rem; line-height: 1.4;",
                                                            },
                                                            "Upgrade your plan for more prepaid delivery (vKM), scheduled deliveries, and more deliveries at the same time.",
                                                        ),
                                                    ),
                                                )
                                              : null,
                                      ),
                                  )
                                : null,

                            // Notes Card
                            h.Card(
                                {
                                    body: h.div(
                                        {
                                            data_intro:
                                                "Add any additional information that the deliverer should consider",
                                            data_step: 2,
                                        },
                                        h.label(
                                            {
                                                class: "form-label small fw-bold text-secondary",
                                                for: "order-notes",
                                            },
                                            "Additional Notes (optional)",
                                        ),
                                        h.textarea({
                                            class: "form-control border-0 bg-light rounded-3 p-3 text-secondary border-focus-primary",
                                            id: "order-notes",
                                            rows: "3",
                                            placeholder:
                                                "Describe specific delivery landmarks or requests...",
                                            oninput: this.method(
                                                "saveOrderNotes",
                                                "${this}",
                                            ),
                                        }),
                                    ),
                                },
                                {
                                    class: "border-0 shadow-sm rounded-4 bg-white",
                                },
                            ),

                            // Order Scheduling Card
                            v(uc.subscription, (sub) => {
                                const hasScheduling = getProvisionBool(sub.value, "order_scheduling", false);
                                if (!hasScheduling) return null;

                                return h.div(
                                    { class: "card border-0 shadow-sm rounded-4 p-4 bg-white mb-4" },
                                    h.h6({ class: "fw-bold text-muted text-uppercase small mb-3 d-flex align-items-center gap-2" }, h.i({ class: "bi bi-calendar-event" }), "Schedule For Later"),
                                    h.div(
                                        { class: "form-check form-switch mb-0" },
                                        h.input({
                                            class: "form-check-input",
                                            type: "checkbox",
                                            role: "switch",
                                            id: "schedule-order-switch",
                                            onchange: this.method("toggleSchedule", "${this}"),
                                        }),
                                        h.label(
                                            { class: "form-check-label small fw-bold text-secondary", for: "schedule-order-switch" },
                                            "Do you want to schedule this order to be placed later?"
                                        )
                                    ),
                                    v(this.shouldSchedule, (scheduled) => {
                                        if (!scheduled.value) return null;
                                        return h.div(
                                            { class: "row g-3 mt-2 pt-2 border-top" },
                                            h.div(
                                                { class: "col-md-6" },
                                                h.label({ class: "form-label small fw-bold text-secondary" }, "Placement Date"),
                                                h.input({
                                                    type: "date",
                                                    class: "form-control border-0 bg-light rounded-3 p-3",
                                                    oninput: this.method("setScheduleDate", "${this}"),
                                                    value: uc.scheduleDate.value || "",
                                                }),
                                                h.p({ class: "form-text small text-muted" }, "We will place your order on this date.")
                                            ),
                                            h.div(
                                                { class: "col-md-6" },
                                                h.label({ class: "form-label small fw-bold text-secondary" }, "Placement Time"),
                                                h.input({
                                                    type: "time",
                                                    class: "form-control border-0 bg-light rounded-3 p-3",
                                                    oninput: this.method("setScheduleTime", "${this}"),
                                                    value: uc.scheduleTime.value || "",
                                                }),
                                                h.p({ class: "form-text small text-muted" }, "We will place your order at this time - this is not delivery time.")
                                            )
                                        );
                                    })
                                );
                            }),

                            // Place Order Button
                            v(uc.cart, (cart) => {
                                let payable = 0;
                                if (cart && cart.value) {
                                    const items = Array.isArray(cart.value)
                                        ? cart.value
                                        : Object.values(cart.value);
                                    items.forEach((item) => {
                                        payable +=
                                            Number(item.qty) *
                                            Number(item.price);
                                    });
                                }

                                return v(this.deliveryInfo, (info) => {
                                    let deliveryFee = 0;
                                    if (info && info.companies) {
                                        const companyKeys = Object.keys(
                                            info.companies,
                                        );
                                        if (companyKeys.length > 0) {
                                            const firstCompany =
                                                info.companies[companyKeys[0]];
                                            const fleet =
                                                uc.fleetType.value ||
                                                Object.keys(
                                                    firstCompany.monetary_fee_per_vehicle,
                                                )[0];
                                            const paymentType =
                                                firstCompany
                                                    .payment_type_per_vehicle?.[
                                                    fleet
                                                ] || "monetary";
                                            if (
                                                paymentType !==
                                                "virtual_distance"
                                            ) {
                                                deliveryFee =
                                                    firstCompany
                                                        .monetary_fee_per_vehicle[
                                                        fleet
                                                    ] || 0;
                                            }
                                        }
                                    }

                                    const total = payable + deliveryFee;
                                    return h.button(
                                        {
                                            class: "btn btn-dark btn-lg w-100 rounded-pill py-3 fw-bold shadow-sm mb-4",
                                            onclick: this.method("placeOrder"),
                                        },
                                        `Place Order and Pay KES ${total.toLocaleString()}`,
                                    );
                                });
                            }),
                        );
                    }),
                ),
            ),
            ...args,
        );
    }

    saveOrderNotes(elem) {
        uc.orderNotes = elem.value;
    }

    toggleSchedule(elem) {
        this.shouldSchedule.value = elem.checked;
        if (!elem.checked) {
            uc.scheduleDate.value = "";
            uc.scheduleTime.value = "";
        }
    }

    setScheduleDate(elem) {
        uc.scheduleDate.value = elem.value;
    }

    setScheduleTime(elem) {
        uc.scheduleTime.value = elem.value;
    }

    placeOrder() {
        if (this.shouldSchedule.value) {
            if (!uc.scheduleDate.value || !uc.scheduleTime.value) {
                Swal.fire({
                    icon: 'error',
                    title: 'Scheduling Required',
                    text: 'Please select both date and time to schedule your order.'
                });
                return;
            }
        }
        fireEvent($e.order.needs.placement);
    }

    $_mounted(componentId) {
        let c = component(componentId);

        if (!c) return;

        if (!c.initialized) {
            c.deliveryCostLoading.listener(c);
            c.deliveryInfo.listener(c);

            // Fetch new delivery prices whenever the addresses list or selected address changes
            uc.addresses.listener(() => {
                c.fetchDeliveryPrice();
            });

            uc.subscription.listener(() => {
                c.fetchDeliveryPrice();
            });

            app("broker").send($e.subscription.needs.active);

            c.initialized = true;
        }

        c.fetchDeliveryPrice();
    }
}

ojs(Checkout);
